import axios from 'axios';
import { getCurrentSession } from './auth';

// The API host is baked in at BUILD time, not read at runtime — the pipeline injects
// REACT_APP_API_URL before `npm run build`. So a bundle built for one environment will
// keep calling that environment's API wherever it is later served from, and the only
// way to point it elsewhere is to rebuild.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

api.interceptors.request.use(async (config) => {
  const session = await getCurrentSession();
  if (session) {
    config.headers.Authorization = `Bearer ${session.token}`;
  }
  return config;
});

export const dashboardApi = {
  getPatientStatus: () => api.get('/api/dashboard/patient-status'),
  getPatientStatusDrill: (stage: string, side: string) =>
    api.get(`/api/dashboard/patient-status/drill/${stage}/${side}`),
};

// meetingsApi (/api/meetings/*) removed 2026-07-26 with the old meetings stack.

// Sentinel "therapist" that carries the roster-wide bonus default rather than one
// person's settings. Must match salary_store.GLOBAL_KEY on the backend.
export const SALARY_GLOBAL_KEY = '__GLOBAL__';

/** What the profit screen is narrowed to. Empty or absent list = no narrowing. */
export interface ProfitFilterSelection {
  payers?: string[];
  services?: string[];
  therapists?: string[];
  clinics?: string[];
}

export const execApi = {
  getStaff: (month: string) =>
    api.get('/api/exec/staff', { params: { month } }),
  getCapacityForecast: () => api.get('/api/exec/capacity-forecast'),
  // Therapist payroll for a month, with the count x tariff breakdown behind
  // each total. Admin-only on the server — this is salary data.
  getSalary: (month: string, all = false) =>
    api.get('/api/exec/salary', { params: { month, ...(all ? { all: true } : {}) } }),
  // Every meeting behind one therapist's month. Loaded on demand — the month
  // payload would triple if this shipped for all of them up front.
  getSalaryLedger: (month: string, therapist: string) =>
    api.get('/api/exec/salary/ledger', { params: { month, therapist } }),
  // A therapist's unanswered comments on their report — fetched when the card opens.
  getSalaryComments: (month: string, therapist: string) =>
    api.get('/api/exec/salary/comments', { params: { month, therapist } }),
  resolveSalaryComments: (month: string, therapist: string, comments: string[], reply?: string) =>
    api.post('/api/exec/salary/comments/resolve', { month, therapist, comments, reply }),
  addSalaryNote: (month: string, therapist: string, note: string) =>
    api.post('/api/exec/salary/comments/note', { month, therapist, note }),
  // Basel's payroll sign-off — the first gate of the month. Set from the SALARY
  // screen (guarded by `salary`); the accounting hub only displays it.
  baselApprove: (month: string, therapist: string, approved: boolean) =>
    api.post('/api/exec/salary/basel-approve', { month, therapist, approved }),
  // Month-close workbook. Self-sufficient by design — it carries the rates that
  // produced the numbers, so an old month stays reproducible after a rate change.
  getSalaryExport: (month: string) =>
    api.get('/api/exec/salary/export', { params: { month }, responseType: 'blob' }),
  // Create/edit a structured additional-payment line-item (insurance/refund/sim/hours/custom).
  saveSalaryItem: (body: {
    item_id?: string | null; therapist: string; month: string;
    category: string; label?: string | null; amount?: number;
    hours?: number | null; rate?: number | null; recurring?: boolean; active?: boolean;
  }) => api.post('/api/exec/salary/item', body),
  // Soft-delete an additional line-item.
  deleteSalaryItem: (body: { item_id: string; month: string; therapist: string }) =>
    api.post('/api/exec/salary/item/delete', body),
  // Override a therapist's pay config — per-meeting tariffs, rates, slot lengths and
  // their bonus policy. Pass SALARY_GLOBAL_KEY to set the roster-wide bonus default.
  saveSalaryRates: (body: { therapist: string; month: string; rates: Record<string, number> }) =>
    api.post('/api/exec/salary/rates', body),
  // Profitability by payer and by service — meetings held x contractual tariff, less
  // what the meeting costs. The headline therapist cost comes from the payroll engine,
  // so this screen and the salary screen can never disagree about it.
  // Four filters, each a comma-separated list; an empty one is omitted entirely so
  // the URL stays readable and the backend's "absent means all" path is the one
  // taken. 🔴 They do not all reach every panel — the response carries
  // `filters_not_applied`, and the screen marks the panels a filter missed.
  getProfit: (range?: { from: string; to: string }, f?: ProfitFilterSelection) =>
    api.get('/api/exec/profit', {
      params: {
        ...(range?.from && range?.to ? { from: range.from, to: range.to } : {}),
        ...(f?.payers?.length ? { payers: f.payers.join(',') } : {}),
        ...(f?.services?.length ? { services: f.services.join(',') } : {}),
        ...(f?.therapists?.length ? { therapists: f.therapists.join(',') } : {}),
        ...(f?.clinics?.length ? { clinics: f.clinics.join(',') } : {}),
      },
    }),
  // The values each filter can take, with the meeting count behind each.
  getProfitFilterOptions: () => api.get('/api/exec/profit/filter-options'),
  // The roster-wide bonus policy currently in force (defaults + any global override).
  getBonusPolicy: () => api.get('/api/exec/salary/bonus-policy'),
  // Set a therapist's accounting profile (employment, VAT status, withholding, invoice type).
  // `month` = the month being viewed — the profile takes effect from there onward.
  saveSalaryProfile: (body: {
    therapist: string; employment: string; vat_registered: boolean; withholding_rate: number;
    invoice_type?: string; month?: string; email?: string; phone?: string;
    therapist_type?: string;
  }) => api.post('/api/exec/salary/profile', body),
  // Archive / delete / restore a therapist FROM `month` onwards — earlier months keep them.
  setRosterState: (body: { therapist: string; state: 'active' | 'archived' | 'deleted'; month: string }) =>
    api.post('/api/exec/salary/roster-state', body),
  // Who is deleted as of this month — the undo list behind the accounting table.
  getDeletedTherapists: (month: string) =>
    api.get('/api/exec/salary/deleted', { params: { month } }),
  // The Hebrew name the clinical system already holds for this therapist (suggestion).
  getHebNameSuggestion: (therapist: string) =>
    api.get('/api/exec/salary/heb-name', { params: { therapist } }),
  // Edit the Hebrew display name of an existing therapist (the English name is the calendar key).
  setDisplayName: (body: { therapist: string; hebrew_name: string }) =>
    api.post('/api/exec/salary/display-name', body),
  // The clinical users who may go on the payroll roster — what the add form picks from.
  clinicalUsers: () => api.get('/api/exec/salary/clinical-users'),
  // Add a therapist to the payroll roster. Normally `system_user_id` from the list
  // above: the id is the key and the name comes from the clinical system. A typed
  // `name` is the manual path only, and must match meetings_raw. Returns a sync check.
  addTherapist: (body: {
    system_user_id?: number; name?: string; hebrew_name?: string; therapist_type?: string;
  }) => api.post('/api/exec/salary/therapist', body),
  // Accounting snapshots — freeze the view to return to a period later.
  // `kind` picks the screen the snapshot belongs to: 'billing' (accounting hub) or
  // 'salary' (Basel's payroll). One table, two lists — see salary_store.SNAPSHOT_KINDS.
  saveBillingSnapshot: (body: { month: string; label: string; payload: string; kind?: string }) =>
    api.post('/api/exec/salary/billing-snapshot', body),
  listBillingSnapshots: (month: string, kind = 'billing') =>
    api.get('/api/exec/salary/billing-snapshots', { params: { month, kind } }),
  getBillingSnapshot: (snapId: string) => api.get('/api/exec/salary/billing-snapshot', { params: { snap_id: snapId } }),
  // Wellness content usage in the mobile app (funnel, per-content table, trend).
  getWellness: (period: string) => api.get('/api/exec/wellness', { params: { period } }),
  // "Especially for you" — the diagnosis→content assignment we own, its schedule, and
  // how many of the assigned patients actually opened content.
  getWellnessSync: (reachDays = 30) =>
    api.get('/api/exec/wellness-sync/status', { params: { reach_days: reachDays } }),
  setWellnessSync: (p: { enabled: boolean; owner: string; hour: number; every_days: number }) =>
    api.post('/api/exec/wellness-sync', p),
  runWellnessSync: () => api.post('/api/exec/wellness-sync/run-now'),
  getFinancial: () => api.get('/api/exec/financial'),
  // Stage-3 accounting state (invoice #, Tal/Lee approvals, paid) per therapist×month.
  getBillingStatus: (month: string) => api.get('/api/exec/salary/billing-status', { params: { month } }),
  setBillingStatus: (body: { therapist: string; month: string; field: string; value: string }) =>
    api.post('/api/exec/salary/billing-status', body),
  // Is SES live yet? The send buttons enable off this.
  getSalaryEmailStatus: () => api.get('/api/exec/salary/email-status'),
  // Pre-close audit — findings to review before approving the month.
  getSalaryAudit: (month: string) => api.get('/api/exec/salary/audit', { params: { month } }),
  // Month close: status (open/sent/closed + approval progress) and the close action.
  getSalaryMonthStatus: (month: string) => api.get('/api/exec/salary/month-status', { params: { month } }),
  closeSalaryMonth: (month: string) => api.post('/api/exec/salary/close', { month }),
  // Email the report link to one therapist (therapist set) or all (omitted).
  sendSalary: (month: string, therapist?: string) =>
    api.post('/api/exec/salary/send', { month, therapist }),
  // Bulk send to a named subset — the rows visible under the table's current
  // filter. The server additionally skips demo rows on any bulk send.
  sendSalaryBulk: (month: string, therapists: string[]) =>
    api.post('/api/exec/salary/send', { month, therapists }),
  // Meetings drill from meetings_raw. backMonths/fwdMonths = arrays of 'YYYY-MM'
  // (multi-select); backward = past months, forward = future months.
  getMeetings: (backMonths: string[], fwdMonths: string[], type: string = 'all', clinics?: string[]) =>
    api.get('/api/exec/meetings', {
      params: {
        back_months: backMonths.join(','), fwd_months: fwdMonths.join(','), type,
        ...(clinics && clinics.length ? { clinics: clinics.join(',') } : {}),
      },
    }),
  // No-show cross-filter breakdown — every bar clickable; each dim excludes its own filter.
  getNoShowBreakdown: (p: {
    backMonths: string[]; clinics?: string[];
    types?: string[]; dows?: string[]; hours?: string[]; therapists?: string[];
  }) =>
    api.get('/api/exec/noshow-breakdown', {
      params: {
        back_months: p.backMonths.join(','),
        ...(p.clinics && p.clinics.length ? { clinics: p.clinics.join(',') } : {}),
        ...(p.types && p.types.length ? { types: p.types.join(',') } : {}),
        ...(p.dows && p.dows.length ? { dows: p.dows.join(',') } : {}),
        ...(p.hours && p.hours.length ? { hours: p.hours.join(',') } : {}),
        ...(p.therapists && p.therapists.length ? { therapists: p.therapists.join(',') } : {}),
      },
    }),
  // Meetings explorer — server-side filtered + paginated per-meeting list (all statuses).
  getMeetingsExplorer: (p: {
    backMonths: string[]; clinics?: string[]; type?: string; status?: string;
    who?: string; therapist?: string; q?: string; page?: number; pageSize?: number;
    typeIds?: number[]; statuses?: string[]; therapists?: string[];
    // The four disjoint kinds: intake | followup | double | reintake. Two of them are
    // derived (payroll's definitions) and cut across meeting-type ids, so they travel
    // separately from typeIds — which now carries the ROLE only.
    kinds?: string[];
    // Column-header controls on the detailed table: two extra value filters that have
    // no chip in the panel, and the sort key+direction.
    patientTypes?: string[]; whos?: string[]; sort?: string;
    // Only meetings whose slot already started. With statuses=['active'] this is the
    // stuck list: the meeting happened (or didn't) and nobody ever closed it.
    pastOnly?: boolean;
    // Day-level span inside the month window (YYYY-MM-DD, inclusive) — read a rate for a week.
    dateFrom?: string; dateTo?: string;
  }) =>
    api.get('/api/exec/meetings-explorer', {
      params: {
        back_months: p.backMonths.join(','),
        ...(p.clinics && p.clinics.length ? { clinics: p.clinics.join(',') } : {}),
        type: p.type ?? 'all', status: p.status ?? 'all', who: p.who ?? 'all',
        therapist: p.therapist ?? '', q: p.q ?? '',
        ...(p.typeIds && p.typeIds.length ? { type_ids: p.typeIds.join(',') } : {}),
        ...(p.statuses && p.statuses.length ? { statuses: p.statuses.join(',') } : {}),
        ...(p.therapists && p.therapists.length ? { therapists: p.therapists.join(',') } : {}),
        ...(p.kinds && p.kinds.length ? { kinds: p.kinds.join(',') } : {}),
        ...(p.patientTypes && p.patientTypes.length ? { patient_types: p.patientTypes.join(',') } : {}),
        ...(p.whos && p.whos.length ? { whos: p.whos.join(',') } : {}),
        ...(p.sort ? { sort: p.sort } : {}),
        ...(p.pastOnly ? { past_only: true } : {}),
        ...(p.dateFrom ? { date_from: p.dateFrom } : {}),
        ...(p.dateTo ? { date_to: p.dateTo } : {}),
        page: p.page ?? 1, page_size: p.pageSize ?? 50,
      },
    }),
  // Report builder — granular therapist×role×month×kind×status×clinic counts (client pivots).
  getMeetingsReport: (months: string[], dateFrom?: string, dateTo?: string) =>
    api.get('/api/exec/meetings-report', {
      params: {
        months: months.join(','),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      },
    }),
  // Stage snapshot — point-in-time stock by milestone, reconstructed as-of any date.
  getStageSnapshot: (asOf: string, clinics?: string[]) =>
    api.get('/api/exec/stage-snapshot', {
      params: { as_of: asOf, ...(clinics && clinics.length ? { clinics: clinics.join(',') } : {}) },
    }),
  // Cohort journey — pick an anchor milestone + month(s) → forward funnel + drop-off reasons.
  getCohortJourney: (anchor: string, months: string[], clinics?: string[], therapists?: string[]) =>
    api.get('/api/exec/cohort-journey', {
      params: {
        anchor, months: months.join(','),
        ...(clinics && clinics.length ? { clinics: clinics.join(',') } : {}),
        ...(therapists && therapists.length ? { therapist: therapists.join(',') } : {}),
      },
    }),
  // Cyclic cohort sequence — next stages after the anchor; CM follow-ups collapse into one
  // stage per cycle (expandable to its 1/2/3/4 split).
  getCohortSequence: (anchor: string, months: string[], clinics?: string[], therapists?: string[]) =>
    api.get('/api/exec/cohort-sequence', {
      params: {
        anchor, months: months.join(','),
        ...(clinics && clinics.length ? { clinics: clinics.join(',') } : {}),
        ...(therapists && therapists.length ? { therapist: therapists.join(',') } : {}),
      },
    }),
  // Patient list behind one "where are they now" cell (stage + outcome) — click-to-list drill.
  getCohortPatients: (anchor: string, months: string[], stage: string, outcome: string, clinics?: string[], therapists?: string[]) =>
    api.get('/api/exec/cohort-patients', {
      params: {
        anchor, stage, outcome, months: months.join(','),
        ...(clinics && clinics.length ? { clinics: clinics.join(',') } : {}),
        ...(therapists && therapists.length ? { therapist: therapists.join(',') } : {}),
      },
    }),
  // Psychiatrist-spine journey — psy follow-ups as milestones + per-cycle CM drilldown.
  getPsyJourney: (months: string[], clinics?: string[]) =>
    api.get('/api/exec/psy-journey', {
      params: {
        months: months.join(','),
        ...(clinics && clinics.length ? { clinics: clinics.join(',') } : {}),
      },
    }),
  // Financial: plan revenue target vs estimated actual (meetings × rate), YTD.
  getRevenueVsTarget: () => api.get('/api/exec/revenue-vs-target'),
  // Staff: active therapists & DONE hours vs plan target (role = psy | cm).
  getStaffTargets: (role: string = 'psy') => api.get('/api/exec/staff-targets', { params: { role } }),
  // New patients = completed CM intake (done) vs plan target, monthly.
  getNewPatients: () => api.get('/api/exec/new-patients'),
  // Active patients = distinct patients treated (done meeting) each month (no target).
  getPatientsTreated: () => api.get('/api/exec/patients-treated'),
  // Meetings done per month vs plan total-meetings target (psy + cm).
  getMeetingsTarget: () => api.get('/api/exec/meetings-target'),
  // Psychiatrist intakes done per month vs plan target (meetingtype 2).
  getPsyIntakesTarget: () => api.get('/api/exec/psy-intakes-target'),
  // Follow-up meetings done vs plan target, per role — meeting-side volume.
  getFollowupsTarget: (role: 'psy' | 'cm') =>
    api.get('/api/exec/followups-target', { params: { role } }),
  // Patients who requested the service (registered = filled the intake questionnaire) per month.
  getRegistrations: () => api.get('/api/exec/registrations'),
  // No-show rate (%) per month — trend + current.
  getNoshowTrend: () => api.get('/api/exec/noshow-trend'),
  // Patient satisfaction (real, DWH view), monthly weighted averages.
  getSatisfaction: () => api.get('/api/exec/satisfaction'),
  // Satisfaction drill — real data: per-period question scores, trend, per-clinic, verbatims.
  // Every month for one therapist — Lee's accounting screen, transposed.
  getBillingHistory: (therapist: string) =>
    api.get('/api/exec/billing/history', { params: { therapist } }),
  getSatisfactionDrill: () => api.get('/api/exec/satisfaction-drill'),
  // The drill carries only the 8 newest comments. This one pages the whole set and
  // filters by score, which is the only way the low scores are reachable at all.
  getSatisfactionFeedback: (p: { page: number; page_size: number; min_score?: number; max_score?: number; worst_first?: boolean;
    doctor?: string; case_manager?: string }) =>
    api.get('/api/exec/satisfaction-drill/feedback', { params: p }),
  // Exports what the FILTER selected, not the current page.
  getSatisfactionFeedbackExport: (p: { min_score?: number; max_score?: number;
    worst_first?: boolean; doctor?: string; case_manager?: string }) =>
    api.get('/api/exec/satisfaction-drill/feedback/export', { params: p, responseType: 'blob' }),
  // On-call schedule (Basel). One GET serves the page: the month's assignments + roster.
  getOncallMonth: (month: string) =>
    api.get('/api/exec/oncall/month', { params: { month } }),
  // Version a whole day: the full list of who covers it ([] = explicitly nobody).
  setOncallDay: (day: string, doctors: string[]) =>
    api.post('/api/exec/oncall/day', { day, doctors }),
  // Add a doctor to the on-call roster. `name` = the salary/calendar English key
  // when the doctor exists there (payroll sync); `system_user_id` = the clinical
  // system's id (clinicui integration).
  addOncallDoctor: (body: { hebrew_name: string; name?: string; system_user_id?: number }) =>
    api.post('/api/exec/oncall/doctors', body),
  // The clinical system's doctor list (with system ids) — the pick-list for adding.
  getOncallCandidates: () => api.get('/api/exec/oncall/candidates'),
  // One doctor across a whole month. mode 'all' overwrites; 'empty' fills holes only.
  assignOncallMonth: (month: string, doctor: string, mode: 'all' | 'empty') =>
    api.post('/api/exec/oncall/assign-month', { month, doctor, mode }),
  // One doctor across a hand-picked set of days (calendar multi-select). Overwrites each.
  assignOncallDays: (days: string[], doctor: string) =>
    api.post('/api/exec/oncall/assign-days', { days, doctor }),
  // Hide a doctor from the picker (or bring them back) — never a delete.
  setOncallDoctorActive: (name: string, active: boolean) =>
    api.post('/api/exec/oncall/doctors/active', { name, active }),
  // Free-text "שאל" pilot — Claude maps the question to a trusted primitive; the primitive computes.
  ask: (q: string) => api.get('/api/exec/ask', { params: { q } }),
  // The dashboard map (permission-filtered) — feeds the assistant's suggested questions.
  getDashboardMap: () => api.get('/api/exec/dashboard-map'),
  // What the logged-in user may open, and at what level. Drives the sidebar and the
  // route guards. Convenience only — every endpoint enforces for itself.
  getMyDashboards: () => api.get('/api/exec/me/dashboards'),
  // The caller's own hub-card order (drag to reorder). Presentation only —
  // the backend drops ids the user doesn't hold and never adds any.
  saveDashboardOrder: (order: string[]) =>
    api.post('/api/exec/me/dashboards/order', { order }),
  // Report generator — automatic mailed reports (catalog, subscriptions, runs).
  getReportsCatalog: () => api.get('/api/exec/reports/catalog'),
  getReportSubscriptions: () => api.get('/api/exec/reports/subscriptions'),
  saveReportSubscription: (body: {
    sub_id?: string | null; report_id: string; recipients: string[];
    cadence: string; send_hour: number; weekdays?: number[];
    day_of_month?: number | null; params?: object; active?: boolean; deleted?: boolean;
  }) => api.post('/api/exec/reports/subscription', body),
  // The report exactly as the mail will carry it (HTML) — rendered in a modal.
  // `params` = a custom report's definition (title + sections), JSON-encoded.
  getReportPreview: (reportId: string, params?: object) =>
    api.get('/api/exec/reports/preview', {
      params: { report_id: reportId, ...(params ? { params: JSON.stringify(params) } : {}) },
      responseType: 'text',
    }),
  // Send right now — to the caller when recipients omitted ("שלח לי לדוגמה").
  sendReportNow: (reportId: string, recipients?: string[], params?: object) =>
    api.post('/api/exec/reports/send-now', {
      report_id: reportId,
      ...(recipients?.length ? { recipients } : {}),
      ...(params ? { params } : {}),
    }),
  getCmBonus: (month: string) => api.get(`/api/exec/cm-bonus?month=${month}`),
  getCmBonusSets: (month: string, cm: string) =>
    api.get('/api/exec/cm-bonus/sets', { params: { month, cm } }),
  getReportRuns: () => api.get('/api/exec/reports/runs'),
  // The master switch — one config row both environments read; `owner` says
  // which environment actually mails (the other stands down).
  setReportsScheduler: (enabled: boolean, owner: string) =>
    api.post('/api/exec/reports/scheduler', { enabled, owner }),
  // Recipient pick-list from user management (enabled users, name + email).
  getReportRecipients: () => api.get('/api/exec/reports/recipients'),
};

export const adminApi = {
  listUsers: () => api.get('/api/admin/users'),
  // What the browser knows and the server cannot: which dashboard was opened, and
  // for how long. Data CHANGES are logged server-side; this is the other half.
  // Admin-only: the data catalogue (what we write / read) and the dashboard overview.
  getCatalog: () => api.get('/api/admin/catalog'),
  getDashboardsOverview: () => api.get('/api/admin/dashboards-overview'),
  // Move a data request along: 'done' (Shachar built it) → verified against BigQuery
  // → 'verified' (Ohad accepts). Neither step changes code on its own.
  setRequestStatus: (requestId: string, status: string, note?: string) =>
    api.post('/api/admin/catalog/request-status', { request_id: requestId, status, note }),
  // The activity log. period: '7d' | '30d' | 'YYYY-MM'; email narrows it to one person.
  getActivity: (period: string, email?: string, env?: string) =>
    api.get('/api/admin/activity', {
      params: { period, ...(email ? { email } : {}), ...(env ? { env } : {}) },
    }),
  logActivity: (body: {
    event: 'login' | 'page_view'; dashboard?: string; path?: string; duration_seconds?: number;
  }) => api.post('/api/admin/activity', body),
  createUser: (body: { email: string; name: string; role: string; grants?: Record<string, string> }) =>
    api.post('/api/admin/users', body),
  setUserRole: (userId: string, role: string) =>
    api.put(`/api/admin/users/${userId}/role`, { role }),
  deleteUser: (userId: string) =>
    api.delete(`/api/admin/users/${userId}`),
  // Disable rather than delete when someone leaves — keeps their audit trail readable.
  setUserEnabled: (userId: string, enabled: boolean) =>
    api.put(`/api/admin/users/${userId}/enabled`, { enabled }),
  resendInvitation: (userId: string) =>
    api.post(`/api/admin/users/${userId}/resend-invitation`),
  // The access email: preview shows exactly what send sends (same composer on the
  // backend). Send is an explicit admin act — saving a role never emails anyone.
  previewAccessEmail: (userId: string) =>
    api.get(`/api/admin/users/${userId}/access-email`),
  sendAccessEmail: (userId: string) =>
    api.post(`/api/admin/users/${userId}/access-email`),
  // Per-user, per-dashboard access.
  // `grants` on createUser writes the day-one access in the same request as the
  // account — creating someone and deciding what they see is one decision.
  listDashboards: () => api.get('/api/admin/dashboards'),
  listGrants: () => api.get('/api/admin/grants'),
  // Saves every changed dashboard for one user in one request — see the admin screen.
  setGrants: (body: { email: string; grants: Record<string, string> }) =>
    api.put('/api/admin/grants', body),
  grantHistory: (email: string) =>
    api.get(`/api/admin/grants/${encodeURIComponent(email)}/history`),
};

export default api;
